import {
  isBooleanAttribute,
  isFunctionAttribute,
  type IAttribute,
  type IMeasure,
} from '../../Interfaces/ILegalTransactionMeasure';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Checkbox,
  useTheme,
  Grid,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { TextAttribute } from './Attributes/TextAttribute';
import { MonetaryValueAttribute } from './Attributes/MonetaryValueAttribute';
import { FunctionAttribute } from './Attributes/FunctionAttribute';
import type { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import type { SetStateAction } from 'react';
import type { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import type { Dispatch } from 'react';
import type { ILegalTransactionGwGFullObject } from '../../Interfaces/ILegalTransactionGwG';
import type { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { NumberAttribute } from './Attributes/NumberAttribute';
import { DateAttribute } from './Attributes/DateAttribute';
import { BooleanAttribute } from './Attributes/BooleanAttribute';

type IProps = {
  measure: IMeasure;
  onUpdateMeasure: (measure: IMeasure) => void;
  idLegalTransaction: number;
  legalTransactionHasSpecialFunctions: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  setLegalTransactionHasSpecialFunctions: Dispatch<
    SetStateAction<ILegalTransaction_has_LegalTransactionSpecialFunction[]>
  >;
  gwgs: ILegalTransactionGwGFullObject[];
  setGwgs: Dispatch<SetStateAction<ILegalTransactionGwGFullObject[]>>;
  legalPhaseFullObjects: ILegalPhaseFullObject[];
  legalRequirementStates: ILegalRequirementState[];
};

export const MeasureTaskCard = ({
  idLegalTransaction,
  legalTransactionHasSpecialFunctions,
  setLegalTransactionHasSpecialFunctions,
  gwgs,
  setGwgs,
  legalPhaseFullObjects,
  legalRequirementStates,
  measure,
  onUpdateMeasure,
}: IProps) => {
  const theme = useTheme();

  const handleToggleCompleted = () => {
    onUpdateMeasure({ ...measure, Completed: !measure.Completed });
  };

  const handleUpdateAttribute = (attribute: IAttribute) => {
    onUpdateMeasure({
      ...measure,
      attributes: measure.attributes.map(a =>
        a.idMeasurementAttribute === attribute.idMeasurementAttribute
          ? attribute
          : a
      ),
    });
  };

  const nonFunctionAttributes = measure.attributes.filter(
    attribute => !isFunctionAttribute(attribute)
  );

  const functionAttributes = measure.attributes.filter(isFunctionAttribute);

  const hasIncompleteAttributes = measure.attributes.some(attribute => {
    if (isFunctionAttribute(attribute)) {
      return false;
    }

    if (isBooleanAttribute(attribute)) {
      return !attribute.Value;
    }

    // Only true if attribute is required and value is undefined, empty or null
    return (
      attribute.Required &&
      (attribute.Value === undefined ||
        attribute.Value === '' ||
        attribute.Value === null)
    );
  });

  return (
    <Accordion
      variant='outlined'
      disableGutters
      defaultExpanded
      sx={{
        backgroundColor: theme.palette.grey[50],
        borderRadius: 2,
        '&:before': {
          display: 'none', // removes the separator line
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}
        >
          <Checkbox
            checked={measure.Completed}
            onChange={handleToggleCompleted}
            onClick={e => e.stopPropagation()}
            disabled={hasIncompleteAttributes}
          />
          <Typography variant='subtitle1'>{measure.Name}</Typography>
          <Chip
            size='small'
            label={
              hasIncompleteAttributes
                ? 'Attribute fehlen'
                : 'Attribute vollständig'
            }
            color={hasIncompleteAttributes ? 'primary' : 'default'}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {nonFunctionAttributes.map(attribute => {
            switch (attribute.MeasurementAttributeType) {
              case 'text':
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={attribute.idMeasurementAttribute}
                  >
                    <TextAttribute
                      attribute={attribute}
                      onUpdateAttribute={handleUpdateAttribute}
                    />
                  </Grid>
                );
              case 'currency':
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={attribute.idMeasurementAttribute}
                  >
                    <MonetaryValueAttribute
                      attribute={attribute}
                      onUpdateAttribute={handleUpdateAttribute}
                    />
                  </Grid>
                );
              case 'number':
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={attribute.idMeasurementAttribute}
                  >
                    <NumberAttribute
                      attribute={attribute}
                      onUpdateAttribute={handleUpdateAttribute}
                    />
                  </Grid>
                );
              case 'date':
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={attribute.idMeasurementAttribute}
                  >
                    <DateAttribute
                      attribute={attribute}
                      onUpdateAttribute={handleUpdateAttribute}
                    />
                  </Grid>
                );
              case 'boolean':
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={attribute.idMeasurementAttribute}
                  >
                    <BooleanAttribute
                      attribute={attribute}
                      onUpdateAttribute={handleUpdateAttribute}
                    />
                  </Grid>
                );
            }
          })}
        </Grid>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {functionAttributes.map(attribute => {
            return (
              <FunctionAttribute
                key={attribute.idMeasurementAttribute}
                attribute={attribute}
                idLegalTransaction={idLegalTransaction}
                legalTransactionHasSpecialFunctions={
                  legalTransactionHasSpecialFunctions
                }
                setLegalTransactionHasSpecialFunctions={
                  setLegalTransactionHasSpecialFunctions
                }
                gwgs={gwgs}
                setGwgs={setGwgs}
                legalPhaseFullObjects={legalPhaseFullObjects}
                legalRequirementStates={legalRequirementStates}
              />
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
